import { useState, useEffect, ReactNode } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { PopUpType } from '../../../common/components/PopUp';
import { LabMats } from '../../../api/labmatsApi';
import { HandoverProtocol } from '../../../api/handoverProtocolsApi';
import { useApi } from '../../../api/useApi';
import { useReferences } from '../../../helpers/context_manager/useReferences';
import { useUser } from '../../../common/components/Header/useUser';
import { toOptions } from '../../../helpers/toOptions';

type HandoverProtocolFormValues = HandoverProtocol & {
  enabled: boolean;
  handover_date: string | null;
  handover_time: string | null;
};

type FormValues = LabMats & {
  femoral_blood_tubes_on: boolean;
  venous_sinus_blood_tubes_on: boolean;
  cardiac_cavity_blood_tubes_on: boolean;
  subclavian_blood_tubes_on: boolean;
  bladder_lavage_ml_on: boolean;
  other_text_on: boolean;
  other_blood_text_on: boolean;
  handover_protocol: HandoverProtocolFormValues;
};

type UseProtocolIdProps = {
  protocolId?: number;
};

export const useLabMaterials = ({ protocolId }: UseProtocolIdProps) => {
  const api = useApi();
  const [message, setMessage] = useState<{ type: PopUpType; message: ReactNode } | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);
  const { references, loading: referencesLoading } = useReferences();
  const user = useUser();

  const defaultValues: FormValues = {
    id: 0,
    autopsy_protocol: protocolId || 0,
    femoral_blood_tubes: 0,
    venous_sinus_blood_tubes: 0,
    cardiac_cavity_blood_tubes: 0,
    subclavian_blood_tubes: 0,
    bladder_lavage_ml: 0,
    femoral_blood_tubes_on: false,
    venous_sinus_blood_tubes_on: false,
    cardiac_cavity_blood_tubes_on: false,
    subclavian_blood_tubes_on: false,
    bladder_lavage_ml_on: false,
    organs: false,
    vitreous_humor: false,
    gastric_content: false,
    hair: false,
    meconium: false,
    urine: false,
    other_text: '',
    other_blood_text: '',
    other_text_on: false,
    other_blood_text_on: false,
    handover_protocol: {
      enabled: false,
      id: 0,
      autopsy: protocolId || 0,
      material_description: '',
      handed_over_at: '',
      handed_over_by: user ? references.employees.find((p) => p.id === user.id)?.id || 0 : 0,
      received_by: 0,
      created_at: '',
      created_by: 0,
      handover_date: '',
      handover_time: ''
    }
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues
  });

  const togglePopup = () => setMessage(undefined);

  useEffect(() => {
    if (!protocolId) {
      setLoading(true);
    }
  }, []);

  useEffect(() => {
    if (protocolId && !referencesLoading && references.genders.length > 0) {
      loadLabMaterials();
    }
  }, [protocolId]);

  const loadLabMaterials = async () => {
    if (!protocolId) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu nebylo nalezeno.' });
      return;
    }

    setLoading(true);

    try {
      const labMatsResponse = await api.labmats.get({ autopsy_protocol: protocolId });
      const labMatsData = labMatsResponse.data ? labMatsResponse.data.results[0] : null;

      const handoverResponse = await api.handoverProtocols.get({ autopsy: protocolId });
      const handoverData = handoverResponse.data ? handoverResponse.data.results[0] : null;

      methods.reset({
        id: labMatsData?.id || 0,
        autopsy_protocol: labMatsData?.autopsy_protocol || protocolId,
        femoral_blood_tubes: labMatsData?.femoral_blood_tubes || 0,
        venous_sinus_blood_tubes: labMatsData?.venous_sinus_blood_tubes || 0,
        cardiac_cavity_blood_tubes: labMatsData?.cardiac_cavity_blood_tubes || 0,
        subclavian_blood_tubes: labMatsData?.subclavian_blood_tubes || 0,
        bladder_lavage_ml: parseFloat(labMatsData?.bladder_lavage_ml as any) || 0,
        femoral_blood_tubes_on: (labMatsData?.femoral_blood_tubes || 0) > 0,
        venous_sinus_blood_tubes_on: (labMatsData?.venous_sinus_blood_tubes || 0) > 0,
        cardiac_cavity_blood_tubes_on: (labMatsData?.cardiac_cavity_blood_tubes || 0) > 0,
        subclavian_blood_tubes_on: (labMatsData?.subclavian_blood_tubes || 0) > 0,
        bladder_lavage_ml_on: parseFloat(labMatsData?.bladder_lavage_ml as any) > 0,
        organs: labMatsData?.organs || false,
        vitreous_humor: labMatsData?.vitreous_humor || false,
        gastric_content: labMatsData?.gastric_content || false,
        hair: labMatsData?.hair || false,
        meconium: labMatsData?.meconium || false,
        urine: labMatsData?.urine || false,
        other_text_on: !!labMatsData?.other_text,
        other_text: labMatsData?.other_text || '',
        other_blood_text_on: !!labMatsData?.other_blood_text,
        other_blood_text: labMatsData?.other_blood_text || '',
        handover_protocol: {
          enabled: !!handoverData,
          id: handoverData?.id || 0,
          autopsy: handoverData?.autopsy || protocolId,
          material_description: handoverData?.material_description || '',
          handed_over_at: handoverData?.handed_over_at || '',
          handed_over_by:
            handoverData?.handed_over_by ||
            (user ? references.employees.find((p) => p.id === user.id)?.id || 0 : 0),
          received_by: handoverData?.received_by || 0,
          created_at: handoverData?.created_at || '',
          created_by: handoverData?.created_by || 0,
          handover_date: parseHandedOverDate(handoverData?.handed_over_at || ''),
          handover_time: parseHandedOverTime(handoverData?.handed_over_at || '')
        }
      });
    } catch (error: any) {
      // TODO LabMaterials may not exist for this protocol yet, handle 404 error gracefully
      if (error.response && error.response.status === 404) {
        methods.reset(defaultValues);
      } else {
        setMessage({
          type: 'error',
          message: 'Nepodařilo se načíst předávací protokol.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const saveLabMaterials = async (data: LabMats) => {
    if (!protocolId) {
      setMessage({
        type: 'error',
        message: 'Chyba: Předávací protokol nelze uložit bez protokolu.'
      });
      return;
    }

    try {
      if (!data.id) {
        data.autopsy_protocol = !data.autopsy_protocol ? protocolId : data.autopsy_protocol;
        const response = await api.labmats.post(data);
        if (response.status === 201) {
          if (response.data?.id) {
            methods.setValue('id', response.data.id);
          }
          setMessage({ type: 'success', message: 'Předávací protokol byl úspěšně uložen.' });
        }
      } else {
        const response = await api.labmats.put(data.id, data);
        if (response.status === 200) {
          setMessage({ type: 'success', message: 'Předávací protokol byl úspěšně aktualizován.' });
        }
      }
    } catch (error: any) {
      let errorMessage = 'Nepodařilo se uložit předávací protokol.';

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errors = Object.entries(errorData)
            .map(
              ([field, messages]) =>
                `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
            )
            .join('\n');

          errorMessage = `Chyba při ukládání:\n${errors}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({ type: 'error', message: errorMessage });
    }
  };

  const saveHandoverProtocol = async (data: HandoverProtocolFormValues): Promise<boolean> => {
    if (!protocolId) {
      setMessage({ type: 'error', message: 'Chyba: Nelze uložit bez protokolu.' });
      return false;
    }

    try {
      if (!data.id) {
        data.autopsy = !data.autopsy ? protocolId : data.autopsy;
        data.handed_over_at =
          composeHandedOverAt(data.handover_date || '', data.handover_time || '') || null;
        const response = await api.handoverProtocols.post(data);
        if (response.data?.id) {
          methods.setValue('handover_protocol.id', response.data.id);
        }
        return response.status === 201 && !!response.data?.id;
      } else {
        data.handed_over_at =
          composeHandedOverAt(data.handover_date || '', data.handover_time || '') || null;
        const response = await api.handoverProtocols.put(data.id, data);
        return response.status === 200;
      }
    } catch (error: any) {
      let errorMessage = 'Nepodařilo se uložit předávací protokol.';

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errors = Object.entries(errorData)
            .map(
              ([field, messages]) =>
                `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
            )
            .join('\n');

          errorMessage = `Chyba při ukládání:\n${errors}`;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({ type: 'error', message: errorMessage });
      return false;
    }
  };

  const validateHandoverProtocol = async (): Promise<boolean> => {
    if (!protocolId) {
      return false;
    }

    const valid = await methods.trigger([
      'handover_protocol.material_description',
      'handover_protocol.handover_date',
      'handover_protocol.handover_time',
      'handover_protocol.handed_over_by',
      'handover_protocol.received_by'
    ]);
    return valid;
  };

  const handleHandoverProtocolPreview = async () => {
    if (!(await validateHandoverProtocol())) {
      return;
    }

    try {
      if (await saveHandoverProtocol(methods.getValues('handover_protocol'))) {
        const handoverProtocolId = methods.getValues('handover_protocol.id');
        const previewUrl = `${process.env.REACT_APP_ROOT_API}/v1/handover-protocols/${handoverProtocolId}/preview/`;
        window.open(previewUrl, '_blank');
      } else {
        setMessage({
          type: 'error',
          message: 'Nepodařilo se uložit předávací protokol pro náhled.'
        });
      }
    } catch (error: any) {
      console.error('Chyba při zobrazení náhledu:', error);
      setMessage({ type: 'error', message: 'Nepodařilo se zobrazit náhled.' });
    }
  };

  const handleHandoverProtocolPrint = async () => {
    if (!(await validateHandoverProtocol())) {
      return;
    }

    try {
      if (await saveHandoverProtocol(methods.getValues('handover_protocol'))) {
        setPrinting(true);
        const handoverProtocolId = methods.getValues('handover_protocol.id');
        const response = await api.handoverProtocols.print(handoverProtocolId);
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url);
          setMessage({ type: 'success', message: 'Předávací protokol byl odeslán k tisku.' });
        } else {
          setMessage({ type: 'error', message: 'Nepodařilo se vytisknout protokol.' });
        }
      }
    } catch (error: any) {
      setMessage({ type: 'error', message: 'Nepodařilo se vytisknout protokol.' });
    } finally {
      setPrinting(false);
    }
  };

  const handleReset = () => {
    methods.reset({
      ...defaultValues,
      handover_protocol: { ...methods.getValues('handover_protocol') }
    });
    setMessage({ type: 'success', message: 'Formulář byl resetován.' });
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (!protocolId) {
      setMessage({ type: 'error', message: 'Chyba: ID protokolu není známo.' });
      return;
    }
    saveLabMaterials(sanitizeLabMatsData(data));

    if (data.handover_protocol.id) {
      saveHandoverProtocol(data.handover_protocol);
    }
  };

  const sanitizeLabMatsData = (data: FormValues): LabMats => {
    const sanitizeNumber = (value: string | number | null | undefined) => {
      if (value === '' || value === 0 || value === '0') {
        return null;
      }
      return Number(value);
    };

    return {
      id: data.id,
      autopsy_protocol: data.autopsy_protocol,
      femoral_blood_tubes: sanitizeNumber(data.femoral_blood_tubes),
      venous_sinus_blood_tubes: sanitizeNumber(data.venous_sinus_blood_tubes),
      cardiac_cavity_blood_tubes: sanitizeNumber(data.cardiac_cavity_blood_tubes),
      subclavian_blood_tubes: sanitizeNumber(data.subclavian_blood_tubes),
      bladder_lavage_ml:
        data.bladder_lavage_ml != null &&
        data.bladder_lavage_ml !== 0 &&
        data.bladder_lavage_ml !== ''
          ? String(data.bladder_lavage_ml)
          : null,
      organs: data.organs,
      vitreous_humor: data.vitreous_humor,
      gastric_content: data.gastric_content,
      hair: data.hair,
      meconium: data.meconium,
      urine: data.urine,
      other_text: data.other_text,
      other_blood_text: data.other_blood_text
    };
  };

  const fields = {
    femoral_blood_tubes: {
      id: 'femoral_blood_tubes',
      name: 'femoral_blood_tubes',
      label: 'Počet zkumavek',
      type: 'number'
    },
    femoral_blood_tubes_on: {
      id: 'femoral_blood_tubes_on',
      name: 'femoral_blood_tubes_on',
      type: 'checkbox'
    },
    venous_sinus_blood_tubes: {
      id: 'venous_sinus_blood_tubes',
      name: 'venous_sinus_blood_tubes',
      label: 'Počet zkumavek',
      type: 'number'
    },
    venous_sinus_blood_tubes_on: {
      id: 'venous_sinus_blood_tubes_on',
      name: 'venous_sinus_blood_tubes_on',
      type: 'checkbox'
    },
    cardiac_cavity_blood_tubes: {
      id: 'cardiac_cavity_blood_tubes',
      name: 'cardiac_cavity_blood_tubes',
      label: 'Počet zkumavek',
      type: 'number'
    },
    cardiac_cavity_blood_tubes_on: {
      id: 'cardiac_cavity_blood_tubes_on',
      name: 'cardiac_cavity_blood_tubes_on',
      type: 'checkbox'
    },
    subclavian_blood_tubes: {
      id: 'subclavian_blood_tubes',
      name: 'subclavian_blood_tubes',
      label: 'Počet zkumavek',
      type: 'number'
    },
    subclavian_blood_tubes_on: {
      id: 'subclavian_blood_tubes_on',
      name: 'subclavian_blood_tubes_on',
      type: 'checkbox'
    },
    bladder_lavage_ml: {
      id: 'bladder_lavage_ml',
      name: 'bladder_lavage_ml',
      label: 'ml',
      type: 'number'
    },
    bladder_lavage_ml_on: {
      id: 'bladder_lavage_ml_on',
      name: 'bladder_lavage_ml_on',
      type: 'checkbox'
    },
    other_text: {
      id: 'other_text',
      name: 'other_text',
      type: 'text'
    },
    other_text_on: {
      id: 'other_text_on',
      name: 'other_text_on',
      type: 'checkbox'
    },
    other_blood_text: {
      id: 'other_blood_text',
      name: 'other_blood_text',
      type: 'text'
    },
    other_blood_text_on: {
      id: 'other_blood_text_on',
      name: 'other_blood_text_on',
      type: 'checkbox'
    },
    organs: {
      id: 'organs',
      name: 'organs',
      type: 'checkbox'
    },
    vitreous_humor: {
      id: 'vitreous_humor',
      name: 'vitreous_humor',
      type: 'checkbox'
    },
    gastric_content: {
      id: 'gastric_content',
      name: 'gastric_content',
      type: 'checkbox'
    },
    hair: {
      id: 'hair',
      name: 'hair',
      type: 'checkbox'
    },
    meconium: {
      id: 'meconium',
      name: 'meconium',
      type: 'checkbox'
    },
    urine: {
      id: 'urine',
      name: 'urine',
      type: 'checkbox'
    },
    handover_protocol: {
      enabled: {
        id: 'handover_protocol_enabled',
        name: 'handover_protocol.enabled',
        type: 'checkbox'
      },
      material_description: {
        id: 'handover_protocol_material_description',
        name: 'handover_protocol.material_description',
        type: 'textarea',
        required: true
      },
      handover_date: {
        id: 'handover_protocol_handover_date',
        name: 'handover_protocol.handover_date',
        type: 'date',
        required: true
      },
      handover_time: {
        id: 'handover_protocol_handover_time',
        name: 'handover_protocol.handover_time',
        type: 'time'
      },
      handed_over_by: {
        id: 'handover_protocol_handed_over_by',
        name: 'handover_protocol.handed_over_by',
        type: 'text',
        required: true
      },
      received_by: {
        id: 'handover_protocol_received_by',
        name: 'handover_protocol.received_by',
        type: 'text',
        required: true
      }
    }
  };

  function parseHandedOverDate(isoString?: string): string {
    if (!isoString) return '';
    try {
      return new Date(isoString).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  function parseHandedOverTime(isoString?: string): string {
    if (!isoString) return '';
    try {
      return new Date(isoString).toTimeString().slice(0, 5);
    } catch {
      return '';
    }
  }

  function composeHandedOverAt(date: string, time: string): string | null {
    if (!date || !time) return null;
    const local = new Date(`${date}T${time}`);
    return local.toISOString();
  }

  const formatISODatetime = (iso: string): string => {
    if (!iso) return '';
    const date = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
      pad(date.getDate()) +
      '.' +
      pad(date.getMonth() + 1) +
      '.' +
      date.getFullYear() +
      ' ' +
      pad(date.getHours()) +
      ':' +
      pad(date.getMinutes())
    );
  };

  return {
    methods,
    fields,
    message,
    loading,
    printing,
    options: {
      employees: toOptions(references.employees)
    },
    togglePopup,
    formatISODatetime,
    handleHandoverProtocolPreview,
    handleHandoverProtocolPrint,
    handleReset,
    onSubmit
  };
};
